import type { OAuthPrefill } from '../types';

/**
 * In-memory store for OAuth prefill payloads passed between `/login` (or
 * `/signup` landing) and the prefilled `/signup` form. The payload includes
 * the provider `uid`, which the backend accepts as the account password
 * during registration. Keeping it in memory (not history.state, not
 * sessionStorage, not the URL) means:
 *
 *   - Reload clears it — the user falls back to the landing screen and
 *     re-runs OAuth instead of carrying a password-equivalent value across
 *     sessions.
 *   - Tab close clears it — no leak on device handoff.
 *   - Copy/paste of a URL carries only an opaque handle, which references
 *     nothing in the recipient's browser.
 *
 * We expose `put`, `take`, and `clear` to the hook and page callers. The
 * store is module-scoped (one shared Map for the whole auth bundle).
 *
 * Entries expire after `ENTRY_TTL_MS` to avoid leaking memory if a caller
 * forgets to `take()` or `clear()` after an aborted flow.
 */

const ENTRY_TTL_MS = 10 * 60 * 1000;
const MAX_ENTRIES = 32;

interface StoredEntry {
  payload: OAuthPrefill;
  createdAt: number;
}

const store = new Map<string, StoredEntry>();

/**
 * Generate an opaque handle with enough entropy that it cannot be guessed
 * during the 10-minute TTL window. 128 bits is overkill for a per-tab
 * lookup, but `crypto.randomUUID` is already available in every browser
 * the app supports and costs nothing.
 *
 * Falls back to Math.random when crypto is unavailable (e.g., very old
 * WebViews) — the value still works as a key, it is just easier to guess.
 */
function generateHandle(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function pruneExpired(): void {
  const now = Date.now();
  for (const [key, entry] of store) {
    if (now - entry.createdAt > ENTRY_TTL_MS) {
      store.delete(key);
    }
  }
  // Cap memory in case a caller loops without cleanup. Evict oldest first.
  while (store.size > MAX_ENTRIES) {
    const firstKey = store.keys().next().value;
    if (firstKey === undefined) break;
    store.delete(firstKey);
  }
}

/**
 * Store a prefill payload and return an opaque handle. The handle is safe
 * to put in URLs or history.state — on its own it references nothing
 * outside this module.
 */
export function putOAuthPrefill(payload: OAuthPrefill): string {
  pruneExpired();
  const handle = generateHandle();
  store.set(handle, { payload, createdAt: Date.now() });
  return handle;
}

/**
 * Read a prefill payload by handle. Returns `null` if the handle is
 * missing, expired, or malformed. Does NOT remove the entry — use
 * `clearOAuthPrefill` to remove an entry explicitly.
 *
 * Kept read-only (non-consuming) so that the same mount can read on
 * initial render and again at submit time (to reconfirm the handle has
 * not expired).
 */
export function peekOAuthPrefill(handle: string | null | undefined): OAuthPrefill | null {
  if (!handle) return null;
  pruneExpired();
  const entry = store.get(handle);
  return entry ? entry.payload : null;
}

/**
 * Remove a prefill entry by handle. Call on successful signup submit or on
 * explicit user cancel.
 */
export function clearOAuthPrefill(handle: string | null | undefined): void {
  if (!handle) return;
  store.delete(handle);
}

/** Test-only. Reset the module-level store between tests. */
export function _resetOAuthPrefillStoreForTest(): void {
  store.clear();
}
